import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { NoData } from "@/components/ui/no-data";
import { schemaBuilderService } from "@/services/schemaBuilderService";
import { SchemaTemplate } from "@/types/schemaBuilder";
import { Copy, Edit, FileJson, MoreVertical, Plus, Trash } from "lucide-react";
import React, { useEffect, useState } from "react";

interface TemplatesListProps {
  onCreateTemplate: () => void;
  onEditTemplate: (template: SchemaTemplate) => void;
}

const TemplatesList: React.FC<TemplatesListProps> = ({
  onCreateTemplate,
  onEditTemplate,
}) => {
  const [templates, setTemplates] = useState<SchemaTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] =
    useState<SchemaTemplate | null>(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportedTemplate, setExportedTemplate] =
    useState<SchemaTemplate | null>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setIsLoading(true);
    try {
      const data = await schemaBuilderService.getTemplates();
      setTemplates(data);
    } catch (error) {
      console.error("Failed to load templates:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (template: SchemaTemplate) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;

    try {
      await schemaBuilderService.deleteTemplate(templateToDelete.id);
      setTemplates(templates.filter((t) => t.id !== templateToDelete.id));
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    } catch (error) {
      console.error("Failed to delete template:", error);
    }
  };

  const handleExportClick = (template: SchemaTemplate) => {
    setExportedTemplate(template);
    setExportDialogOpen(true);
  };

  const handleCopyToClipboard = () => {
    if (!exportedTemplate) return;

    const jsonString = JSON.stringify(exportedTemplate.schema, null, 2);
    navigator.clipboard.writeText(jsonString);
    // Show success notification
  };

  const handleDuplicateTemplate = async (template: SchemaTemplate) => {
    try {
      const newTemplate = await schemaBuilderService.createTemplate({
        name: `${template.name} (Copy)`,
        description: template.description,
        schema: template.schema,
        organizationId: template.organizationId,
        createdBy: template.createdBy,
      });

      setTemplates([newTemplate, ...templates]);
    } catch (error) {
      console.error("Failed to duplicate template:", error);
    }
  };

  return (
    <div className="w-full py-6 bg-background text-foreground">
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        <div className="flex justify-end items-center mb-6">
          <Button variant="primaryOutline" onClick={onCreateTemplate}>
            <Plus className="mr-2 h-4 w-4" /> Create Template
          </Button>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner text="Loading templates..." />
          </div>
        ) : templates.length === 0 ? (
          <NoData
            icon={<FileJson className="h-12 w-12 text-muted-foreground/50" />}
            title="No templates found"
            description="Create your first template to get started."
            actionButton={
              <Button
                variant="primaryOutline"
                onClick={onCreateTemplate}
                className="mt-2"
              >
                <Plus className="mr-2 h-4 w-4" />
                Create Your First Template
              </Button>
            }
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template) => (
              <Card key={template.id} className="flex flex-col">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle>{template.name}</CardTitle>
                      <CardDescription className="mt-1">
                        {template.description || "No description"}
                      </CardDescription>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => onEditTemplate(template)}
                        >
                          <Edit className="mr-2 h-4 w-4" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDuplicateTemplate(template)}
                        >
                          <Copy className="mr-2 h-4 w-4" /> Duplicate
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleExportClick(template)}
                        >
                          <FileJson className="mr-2 h-4 w-4" /> Export JSON
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDeleteClick(template)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash className="mr-2 h-4 w-4" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent className="flex-1">
                  <div className="text-sm text-muted-foreground">
                    <p>
                      Created:{" "}
                      {new Date(template.createdAt).toLocaleDateString()}
                    </p>
                    <p>
                      Fields: {Object.keys(template.schema.properties).length}
                    </p>
                  </div>
                </CardContent>
                <CardFooter className="pt-2">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => onEditTemplate(template)}
                  >
                    <Edit className="mr-2 h-4 w-4" /> Edit Template
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Template</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{templateToDelete?.name}"? This
                action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDeleteConfirm}>
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Export Dialog */}
        <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Export JSON Schema</DialogTitle>
              <DialogDescription>
                Copy the JSON schema for "{exportedTemplate?.name}" to use in
                your application.
              </DialogDescription>
            </DialogHeader>
            <div className="my-4">
              <pre className="bg-muted p-4 rounded-md overflow-auto max-h-96">
                {exportedTemplate
                  ? JSON.stringify(exportedTemplate.schema, null, 2)
                  : ""}
              </pre>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setExportDialogOpen(false)}
              >
                Close
              </Button>
              <Button onClick={handleCopyToClipboard}>Copy to Clipboard</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default TemplatesList;
